import React, { useEffect, useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import {
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom'
import branch from 'branch-sdk'
import { ReactNotifications, Store } from 'react-notifications-component'
import 'react-notifications-component/dist/theme.css'
import 'react-circular-progressbar/dist/styles.css'
import { ThemeProvider } from 'styled-components'
import ResetPassword from 'containers/Auth/components/ResetPassword'
import Signup from 'containers/Auth/Signup'
import ForgotPassword from 'containers/Auth/components/ForgotPassword'
import PageView from '../components/PageView/PageView'
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings'
import { initFeatureFlags } from '@dsny/dsny-feature-flag-library'
import { FeatureFlagsProvider } from 'contexts/FeatureFlagsContext'

import {
  Fallback,
  DownloadManager,
  NotificationView,
  LogoutDisclaimerModal,
  ConnectionWarning
} from 'components'
import {
  ReleasesView,
  Audio,
  Search,
  Flagged,
  Archived,
  RecentlyPlayed,
  Playlist,
  PlaylistDetail
} from 'containers'
import { checkTokenExpiry, tokenValidator } from 'services/tokenValidator'
import { ProfileService } from 'services'
import { thunks as OptionsThunks } from 'reducers/Options'
import { thunks as SettingThunks } from 'reducers/Setting'
import ProfileActions from 'reducers/Profile'
import GlobalActions from 'reducers/GlobalState'
import { thunks as PlaylistThunks } from 'reducers/PlayList'
import { thunks as AuthThunks } from 'reducers/Auth'
import NotificationActions from 'reducers/Notification'
import { NotificationType } from 'constants/enums'
import { RESPONSIVE_BREAKPOINT_PAGES } from 'constants/responsiveBreakpoints'
import theme from 'styles/theme'
import { notificationDuration } from 'config'
import { LEGACY_BREAKPOINT_QUERIES } from 'styles/breakpoints'
import useBreakpoint from 'utils/hooks/useBreakpoint'
import Login from './Auth/Login'
import ReleaseInformation from './ReleaseInformation/ReleaseInformation'
import CreatePlaylistModal from 'components/Modals/CreatePlaylistModal'
import SignoutModal from 'components/Modals/SignoutModal'
import ClearQueueModal from 'components/Modals/ClearQueueModal'
import ShareReleaseModal from 'components/Modals/ShareReleaseModal'
import ResetPasswordConfirmationModal from 'components/Modals/ResetPasswordConfirmationModal'
import SignoutAllSessionsModal from 'components/Modals/SignoutAllSessionsModal'
import ConnectEmailModal from 'components/Modals/ConnectEmailModal'
import InviteConnection from 'containers/InviteConnection/InviteConnection'
import GlobalOverlaysWithRollout from 'components/GlobalOverlaysWithRollout'
import withRouter from 'utils/hocs/withRouter'
import 'animate.css/animate.min.css'
import MyNetwork from './MyNetwork/MyNetwork'
import { Account } from './Account'
import { ProfileMobileWrapper } from './Profile'
import { resetSessionState } from 'utils/authSession'
import useToken from '../utils/hooks/useToken'
import { AccountManagementMobileWrapper } from './AccountManagement'
import {
  SettingsAndPreferencesMobileWrapper,
  MergeEmailRedirect,
  SplitEmailRedirect
} from './SettingsAndPreferences'

const mapStateToProps = ({
  authentication,
  notification,
  profile,
  releases,
  modal,
  options
}) => ({
  loading: authentication.loading,
  token: authentication.accessToken,
  showLogoutDisclaimerModal: authentication.showLogoutDisclaimerModal,
  showSignoutModal: modal.showSignoutModal,
  showClearQueueModal: modal.showClearQueueModal,
  showInviteConnectionModal: modal.showInviteModal,
  showShareReleaseModal: modal.showShareReleaseModal,
  showResetPasswordConfirmationModal: modal.showResetPasswordConfirmationModal,
  showSignoutAllSessionsModal: modal.showSignoutAllSessionsModal,
  showConnectEmailModal: modal.showConnectEmailModal,
  genres: options.genres,
  markets: options.markets,
  notification: notification.notification,
  profile: profile.profile,
  releases,
  modal
})

const redirectPathList = [
  '/releases/releaseInfo/',
  '/playlists/',
  '/account',
  '/my-network',
  '/recently_played',
  '/flagged',
  '/archived',
  '/settings/'
]

const getRedirectTarget = (redirectPath) => {
  if (!redirectPath) return null

  switch (redirectPath.link_type) {
    case 'new_release':
      return `/releases/releaseInfo/${redirectPath.release_id}`
    case 'path':
      return `${redirectPath.release_id}`
    default:
      return null
  }
}

const App = ({
  token,
  modal,
  notification,
  showLogoutDisclaimerModal = false,
  showSignoutModal = false,
  showClearQueueModal = false,
  showInviteConnectionModal = false,
  showShareReleaseModal = false,
  showResetPasswordConfirmationModal = false,
  showSignoutAllSessionsModal = false,
  showConnectEmailModal = false,
  dispatch
}) => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [isOnline, setIsOnline] = useState(true)
  const [redirectPath, setRedirectPath] = useState(null)
  const [showConnectionWarning, setShowConnectionWarning] = useState(false)
  const [featureFlagsReady, setFeatureFlagsReady] = useState(false)
  const notifViewIdTable = useRef({})
  const location = useLocation()
  const { getUser } = useToken()
  const user = getUser()
  // isIpadView must flip at 1280px for legacy and v2 consumers alike, so it
  // cannot reuse useDesktopMediaQuery, whose legacy query starts at 1440px.
  const isDesktopUp = useBreakpoint('desktopUp', {
    legacyQuery: LEGACY_BREAKPOINT_QUERIES.desktopUp
  })

  const { client } = useClientAsyncInit(
    import.meta.env.VITE_STATSIG_CLIENT_API,
    {
      userID: user?.playerid?.toString() || 'anonymous',
      email: user?.email || undefined
    },
    {
      environment: { tier: import.meta.env.VITE_STATSIG_ENV }
    }
  )

  const tokenStatus = token ? checkTokenExpiry(token) : null
  const loggedIn = !!token && !!tokenStatus && !tokenStatus.expired
  const lastSyncedStatsigUserRef = useRef(null)

  const redirectToPath = useCallback(
    (path) => {
      localStorage.removeItem('redirect')
      setRedirectPath(null)

      const currentPath = `${location.pathname}${location.search}${location.hash}`
      if (path === currentPath) {
        return
      }

      navigate(path, { replace: true })
    },
    [location.hash, location.pathname, location.search, navigate]
  )

  useEffect(() => {
    if (!token || !tokenStatus?.invalid) {
      return
    }

    resetSessionState(dispatch)
  }, [token, tokenStatus?.invalid])

  useEffect(() => {
    const handleStorage = (event) => {
      const sessionKeyChanged =
        event.key === 'accessToken' || event.key === 'refreshToken'
      const sessionRemoved = event.oldValue && event.newValue === null

      if (!token || !sessionKeyChanged || !sessionRemoved) {
        return
      }

      resetSessionState(dispatch)
    }

    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [dispatch, token])

  useEffect(() => {
    const statsigUser = getUser()
    const userID = statsigUser?.playerid?.toString()
    const email = statsigUser?.email

    if (!loggedIn || !userID) {
      lastSyncedStatsigUserRef.current = null
      return
    }

    const syncKey = `${userID}:${email || ''}`

    if (lastSyncedStatsigUserRef.current === syncKey) {
      return
    }

    client
      .updateUserAsync({ userID, email })
      .then((details) => {
        if (details?.success) {
          lastSyncedStatsigUserRef.current = syncKey
          return
        }
        console.warn('Statsig user update did not complete successfully')
      })
      .catch((error) => {
        console.error('Failed to update Statsig user', error)
      })
  }, [client, loggedIn, token])

  useEffect(() => {
    const path = location.pathname

    const handleRedirect = () => {
      if (!loggedIn && redirectPathList.some((p) => path.includes(p))) {
        localStorage.setItem(
          'redirect',
          JSON.stringify({
            link_type: 'path',
            release_id: path
          })
        )
      }
    }

    if (path.startsWith('/authverify')) {
      setIsLoading(false)
      return
    }

    branch.init(import.meta.env.VITE_BRANCH_KEY || '', (err, data) => {
      if (data?.data_parsed?.link_type === 'new_release') {
        localStorage.setItem(
          'redirect',
          JSON.stringify({
            link_type: 'new_release',
            release_id: data.data_parsed.release_id
          })
        )
      }

      handleRedirect()
      const redirectData = localStorage.getItem('redirect')
      if (redirectData) {
        try {
          setRedirectPath(JSON.parse(redirectData))
        } catch (error) {
          console.error('Failed to parse redirect data:', error)
          localStorage.removeItem('redirect')
        }
      }

      setIsLoading(false)
    })
  }, [location.pathname, loggedIn])

  useEffect(() => {
    if (isLoading || !loggedIn || redirectPath == null) {
      return undefined
    }

    const targetPath = getRedirectTarget(redirectPath)
    if (!targetPath) {
      localStorage.removeItem('redirect')
      setRedirectPath(null)
      return undefined
    }

    const timeoutId = setTimeout(() => {
      redirectToPath(targetPath)
    }, 200)

    return () => clearTimeout(timeoutId)
  }, [isLoading, loggedIn, redirectPath, redirectToPath])

  useEffect(() => {
    if (loggedIn) {
      try {
        ProfileService.getProfile()
          .then((response) => {
            dispatch(ProfileActions.didGetProfile(response.data))
          })
          .catch((error) => {
            console.error('App.jsx: Error loading profile:', error)
          })

        dispatch(OptionsThunks.getOptions())
        dispatch(PlaylistThunks.listAll())
        dispatch(SettingThunks.getLanguageOptions())

        try {
          dispatch(AuthThunks.getConnections())
        } catch (error) {
          console.error('Error loading connections:', error)
        }

        try {
          dispatch(AuthThunks.processAndRetrieveConnections())
            .then((response) => {
              const { data } = response?.data || {}
              if (data?.connected?.length > 0) {
                dispatch(
                  NotificationActions.broadcastMessage(
                    `${data.connected.map((connection) => connection)} `,
                    NotificationType.connectionAccepted
                  )
                )
              }
            })
            .catch((error) => {
              console.error('Error processing pending connections:', error)
            })
        } catch (error) {
          console.error(
            'Error dispatching processAndRetrieveConnections:',
            error
          )
        }
      } catch (error) {
        console.error('App.jsx: Critical error in useEffect:', error)
      }
    }
  }, [loggedIn])

  useEffect(() => {
    dispatch(GlobalActions.setIsIpadView(!isDesktopUp))
  }, [dispatch, isDesktopUp])

  useEffect(() => {
    const handleConnectionChange = (event) => {
      if (event.type === 'offline') handleOffline()
      else if (event.type === 'online') handleOnline()
    }

    const handleWindowFocus = () => {
      tokenValidator()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        tokenValidator()
      }
    }

    const handleOffline = () => {
      console.warn('Working offline')
      setIsOnline(false)
      setShowConnectionWarning(true)
    }

    const handleOnline = () => {
      console.warn('Back online')
      setIsOnline(true)
      hideConnectionWarning()
      tokenValidator()
    }

    const hideConnectionWarning = () => {
      setTimeout(() => setShowConnectionWarning(false), 500)
    }

    window.addEventListener('offline', handleConnectionChange)
    window.addEventListener('online', handleConnectionChange)
    window.addEventListener('focus', handleWindowFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('offline', handleConnectionChange)
      window.removeEventListener('online', handleConnectionChange)
      window.removeEventListener('focus', handleWindowFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearTimeout(hideConnectionWarning)
    }
  }, [])

  useEffect(() => {
    if (notification) {
      addNotification(notification)
    }
  }, [notification])

  useEffect(() => {
    const product = import.meta.env.VITE_FEATURE_FLAG_PRODUCT || 'player'
    const rawBaseUrl =
      import.meta.env.VITE_FEATURE_FLAG_BASE_URL ||
      import.meta.env.VITE_BASE_URL
    const baseUrl = rawBaseUrl
      ? rawBaseUrl.replace(/\/playerapi\/?$/i, '').replace(/\/$/, '')
      : rawBaseUrl

    if (!baseUrl) {
      console.warn(
        'Feature flag base URL is not configured. Skipping initialization.'
      )
      setFeatureFlagsReady(false)
      return
    }

    initFeatureFlags(product, baseUrl)
      .then(() => setFeatureFlagsReady(true))
      .catch((error) => {
        console.error('Failed to initialize feature flags', error)
        setFeatureFlagsReady(false)
      })
  }, [])
  const handleRemoveNotification = (id) => {
    const notifViewId = notifViewIdTable.current[id]
    Store.removeNotification(notifViewId)
  }

  const addNotification = (notification) => {
    const { id } = notification
    const notifViewId = Store.addNotification({
      content: renderNotification(notification),
      insert: 'bottom',
      container: 'bottom-right',
      animationIn: ['animate__animated', 'animate__fadeIn'],
      animationOut: ['animate__animated', 'animate__fadeOut'],
      slidingEnter: { duration: 200 },
      slidingExit: { duration: 200 },
      dismiss: { duration: notificationDuration },
      dismissable: { click: true },
      width: 400
    })

    notifViewIdTable.current[id] = notifViewId
  }

  const renderNotification = (notification) => (
    <NotificationView
      notification={notification}
      onRemove={handleRemoveNotification}
    />
  )

  const toggleChatbox = (value) => {
    if ($zoho && $zoho.salesiq && $zoho.salesiq.floatbutton) {
      $zoho.salesiq.floatbutton.visible(value ? 'show' : 'hide')
    }
    setTimeout(() => {
      if ($zoho && $zoho.salesiq && $zoho.salesiq.floatbutton) {
        $zoho.salesiq.floatbutton.visible(value ? 'show' : 'hide')
      }
    }, 100)
  }

  const pathname = window.location.pathname

  if (!isLoading && (pathname.includes('signup') || !loggedIn)) {
    toggleChatbox(true)
  } else {
    toggleChatbox(false)
  }

  if (!isLoading) {
    return (
      <FeatureFlagsProvider value={{ ready: featureFlagsReady }}>
        <StatsigProvider client={client}>
          <ThemeProvider theme={theme}>
            <ReactNotifications />
            <PageView />
            {showConnectionWarning && <ConnectionWarning isOnline={isOnline} />}
            <Routes>
              <Route
                path="/"
                element={
                  <Login responsiveBreakpointsRollout loggedIn={loggedIn} />
                } />
              <Route
                path="/authverify/:token"
                element={
                  <Login
                    responsiveBreakpointsRollout
                    responsiveBreakpointsPage={
                      RESPONSIVE_BREAKPOINT_PAGES.AUTH_VERIFY
                    } />
                } />
              <Route
                path="/signin/:signInConnectionToken"
                element={
                  <Login
                    responsiveBreakpointsRollout
                    responsiveBreakpointsPage={
                      RESPONSIVE_BREAKPOINT_PAGES.CONNECTION_SIGN_IN
                    } />
                } />
              <Route
                path="/resetpwd/:token"
                element={
                  <ResetPassword
                    responsiveBreakpointsRollout
                    responsiveBreakpointsPage={
                      RESPONSIVE_BREAKPOINT_PAGES.RESET_PASSWORD
                    } />
                } />
              <Route
                path="/forgot-password"
                element={
                  <ForgotPassword
                    responsiveBreakpointsRollout
                    responsiveBreakpointsPage={
                      RESPONSIVE_BREAKPOINT_PAGES.FORGOT_PASSWORD
                    } />
                } />
              <Route
                path="/signup/:token?"
                element={
                  <Signup
                    responsiveBreakpointsRollout
                    responsiveBreakpointsPage={
                      RESPONSIVE_BREAKPOINT_PAGES.SIGNUP
                    } />
                } />

              {!loggedIn && <Route path="*" element={<Navigate to="/" />} />}
              {loggedIn && (
                <>
                  <Route path="/releases" element={<ReleasesView />} />
                  <Route path="/playlists/:id" element={<PlaylistDetail />} />
                  <Route path="/playlists" element={<Playlist />} />
                  <Route path="/flagged" element={<Flagged />} />
                  <Route path="/archived" element={<Archived />} />
                  <Route path="/recently_played" element={<RecentlyPlayed />} />
                  <Route
                    path="/merge/:token"
                    element={<MergeEmailRedirect />}
                  />
                  <Route
                    path="/split/:token"
                    element={<SplitEmailRedirect />}
                  />
                  <Route path="/account" element={<Account />} />
                  <Route path="/account/:subroute" element={<Account />} />
                  <Route path="/profile" element={<ProfileMobileWrapper />} />
                  <Route
                    path="/account-management"
                    element={<AccountManagementMobileWrapper />}
                  />
                  <Route
                    path="/settings-preferences"
                    element={<SettingsAndPreferencesMobileWrapper />}
                  />
                  <Route path="/my-network" element={<MyNetwork />} />
                  <Route
                    path="/releases/releaseInfo/:id"
                    element={<ReleaseInformation />}
                  />
                </>
              )}
              <Route path="/search/:category?/:profile?" element={<Search />} />
              {loggedIn && <Route path="*" element={<Fallback />} />}
            </Routes>
            <Audio />
            <DownloadManager />
            {showLogoutDisclaimerModal && <LogoutDisclaimerModal />}
            {showInviteConnectionModal && <InviteConnection />}
            <GlobalOverlaysWithRollout>
              {modal.showPlaylistModal && <CreatePlaylistModal />}
              {showSignoutModal && <SignoutModal />}
              {showClearQueueModal && <ClearQueueModal />}
              {showShareReleaseModal && <ShareReleaseModal />}
              {showResetPasswordConfirmationModal && (
                <ResetPasswordConfirmationModal />
              )}
              {showSignoutAllSessionsModal && <SignoutAllSessionsModal />}
              {showConnectEmailModal && <ConnectEmailModal />}
            </GlobalOverlaysWithRollout>
          </ThemeProvider>
        </StatsigProvider>
      </FeatureFlagsProvider>
    )
  } else {
    return <></>
  }
}

App.propTypes = {
  token: PropTypes.string,
  modal: PropTypes.object,
  notification: PropTypes.object,
  showLogoutDisclaimerModal: PropTypes.bool,
  showSignoutModal: PropTypes.bool,
  location: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  profile: PropTypes.object,
  genres: PropTypes.array
}

export default withRouter(connect(mapStateToProps)(App))
